import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { shallowEqual } from 'react-redux';
import { TextAreaCardDisplay } from '../../components/cards/textAreaCardDisplay';
import { HtmlRenderer } from '../../components/htmlAreas/htmlRenderer';
import { TwemojiWrapper } from '../../components/twemojiWrapper';
import { ArrowLeftThickIcon } from '../../icons/arrowLeftThickIcon';
import { ArrowRightThickIcon } from '../../icons/arrowRightThickIcon';
import { useAppSelector } from '../../redux/store';
import { CHAT_PANEL_BACKGROUND_COLOR, CHAT_PANEL_MESSAGES_BACKGROUND_COLOR, CHAT_PANEL_MESSAGES_HAND_REVEAL_BACKGROUND_COLOR, CHAT_PANEL_MESSAGES_SUBTLE_BACKGROUND_COLOR, DARK_GREEN_COLOR, DARK_RED_COLOR } from '../../styles/colors';
import { assertUnreachable } from '../../utils/tsUtils';
const ACTION_BORDER_RADIUS = 8;
const ActionResultContainer = styled.div `
  background-color: ${CHAT_PANEL_MESSAGES_SUBTLE_BACKGROUND_COLOR};
  margin: 2px;
  padding: 4px;
  border-radius: ${ACTION_BORDER_RADIUS}px;
  color: #444444;
  font-weight: bold;
`;
const ActionErrorContainer = styled.div `
  background-color: #aaa;
  margin: 2px;
  padding: 4px;
  border-radius: ${ACTION_BORDER_RADIUS}px;
  color: #880000;
  font-weight: bold;
`;
const PlayerEntryContainer = styled.div `
  background-color: ${CHAT_PANEL_MESSAGES_SUBTLE_BACKGROUND_COLOR};
  margin: 2px 0;
  padding: 2px 8px 2px 2px;
  border-top-right-radius: ${ACTION_BORDER_RADIUS}px;
  border-bottom-right-radius: ${ACTION_BORDER_RADIUS}px;
  color: #444444;
  font-size: 75%;
  font-weight: bold;
  // display: inline-block allows colored background to constrain to the contents, including Material UI buttons, which had the habit of "spilling over":
  display: inline-block;
`;
const HandRevealContainer = styled.div `
  background-color: ${CHAT_PANEL_MESSAGES_HAND_REVEAL_BACKGROUND_COLOR};
  margin: 2px;
  padding: 2px 8px;
  border-radius: ${ACTION_BORDER_RADIUS}px;
  border: 1px solid ${CHAT_PANEL_BACKGROUND_COLOR};
  color: #444444;
  font-size: 85%;
  font-weight: bold;
  // display: inline-block allows colored background to constrain to the contents, including Material UI buttons, which had the habit of "spilling over":
  display: inline-block;
  align-items: center;
`;
const TYPOGRAPHY_CSS = css `
  line-height: 1.2em;
`;
const ARROW_PROPS = {
    sx: { fontSize: '16px', verticalAlign: 'middle' }
};
function getChatItemContents(item, isFourColorDeckEnabled) {
    switch (item.type) {
        case 'chat': {
            return _jsx(HtmlRenderer, { html: item.html });
        }
        case 'result': {
            return (_jsx(ActionResultContainer, { children: _jsx(HtmlRenderer, { html: item.html }) }));
        }
        case 'error': {
            return (_jsx(ActionErrorContainer, { children: _jsx(HtmlRenderer, { html: item.html }) }));
        }
        case 'player-enter': {
            return (_jsxs(PlayerEntryContainer, { children: [_jsx(ArrowRightThickIcon, { ...ARROW_PROPS, style: { color: DARK_GREEN_COLOR } }), _jsx(HtmlRenderer, { html: item.html })] }));
        }
        case 'player-leave': {
            return (_jsxs(PlayerEntryContainer, { children: [_jsx(ArrowLeftThickIcon, { ...ARROW_PROPS, style: { color: DARK_RED_COLOR, opacity: 0.7 } }), _jsx(HtmlRenderer, { html: item.html })] }));
        }
        case 'hand-reveal': {
            return (_jsxs(HandRevealContainer, { children: [item.username, " reveals", ' ', item.cards.map((card, i) => (_jsx(TextAreaCardDisplay, { card: card, isFourColorDeck: isFourColorDeckEnabled }, i))), ' on ', item.communityCards.map((communityCard, i) => (_jsx(TextAreaCardDisplay, { card: communityCard.card, isFourColorDeck: isFourColorDeckEnabled }, i))), item.rabbits.map((rabbit, i) => (_jsx(TextAreaCardDisplay, { card: rabbit.card, overrideColor: '#555555', overrideBackgroundColor: CHAT_PANEL_MESSAGES_BACKGROUND_COLOR }, i))), ' for ', item.handName, "."] }));
        }
        default: {
            assertUnreachable(item);
        }
    }
}
const ChatItemDisplay = React.memo(({ chatItemId, isFourColorDeckEnabled }) => {
    const chatItem = useAppSelector((state) => state.chat.itemsById[chatItemId], shallowEqual);
    if (!chatItem) {
        return null;
    }
    return (_jsx(TwemojiWrapper, { children: _jsx(Typography, { component: 'div', css: TYPOGRAPHY_CSS, className: 'scaling-panel-contents', children: getChatItemContents(chatItem, isFourColorDeckEnabled) }) }));
});
ChatItemDisplay.displayName = 'ChatItemDisplay';
export { ChatItemDisplay };
